package fr.frux.feature.site.detail

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.selection.selectable
import androidx.compose.foundation.selection.selectableGroup
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.RadioButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.semantics
import fr.frux.core.designsystem.components.FruxButton
import fr.frux.core.designsystem.components.FruxMinTouchTarget
import fr.frux.core.designsystem.components.FruxModalBottomSheet
import fr.frux.core.designsystem.components.FruxTextField
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.feature.site.resources.Res
import fr.frux.feature.site.resources.site_report
import fr.frux.feature.site.resources.site_report_details
import fr.frux.feature.site.resources.site_report_gone
import fr.frux.feature.site.resources.site_report_other
import fr.frux.feature.site.resources.site_report_send
import fr.frux.feature.site.resources.site_report_wrong_access
import fr.frux.feature.site.resources.site_report_wrong_owner
import fr.frux.shared.content.SiteLimits
import fr.frux.shared.domain.model.SiteReportKind
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

private fun SiteReportKind.labelRes(): StringResource =
    when (this) {
        SiteReportKind.GONE -> Res.string.site_report_gone
        SiteReportKind.WRONG_OWNER -> Res.string.site_report_wrong_owner
        SiteReportKind.WRONG_ACCESS -> Res.string.site_report_wrong_access
        SiteReportKind.OTHER -> Res.string.site_report_other
    }

/**
 * Formulaire « Signaler ce lieu » : un motif, une précision obligatoire pour « Autre chose ». Le
 * motif propriétaire n'a de sens que si quelqu'un s'est déclaré propriétaire.
 */
@Composable
fun SiteReportForm(
    hasOwner: Boolean,
    draft: SiteReportDraft,
    onDraft: (SiteReportDraft) -> Unit,
    onSubmit: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val kinds = SiteReportKind.entries.filter { it != SiteReportKind.WRONG_OWNER || hasOwner }
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm)) {
        Text(
            text = stringResource(Res.string.site_report),
            style = MaterialTheme.typography.titleLarge,
            modifier = Modifier.semantics { heading() },
        )
        Column(Modifier.selectableGroup()) {
            kinds.forEach { option ->
                ReportKindRow(
                    label = stringResource(option.labelRes()),
                    selected = draft.kind == option,
                    onSelect = { onDraft(draft.copy(kind = option)) },
                )
            }
        }
        FruxTextField(
            value = draft.details,
            onValueChange = { onDraft(draft.copy(details = it)) },
            label = stringResource(Res.string.site_report_details),
            singleLine = false,
            maxLength = SiteLimits.REPORT_DETAILS_MAX,
            modifier = Modifier.fillMaxWidth(),
        )
        FruxButton(
            text = stringResource(Res.string.site_report_send),
            onClick = onSubmit,
            enabled = draft.canSend,
            modifier = Modifier.fillMaxWidth(),
        )
    }
}

@Composable
private fun ReportKindRow(
    label: String,
    selected: Boolean,
    onSelect: () -> Unit,
) {
    Row(
        modifier =
            Modifier
                .fillMaxWidth()
                .heightIn(min = FruxMinTouchTarget)
                .selectable(selected = selected, onClick = onSelect, role = Role.RadioButton),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        RadioButton(selected = selected, onClick = null)
        Spacer(Modifier.width(FruxSpacing.sm))
        Text(label, style = MaterialTheme.typography.bodyLarge)
    }
}

/** [SiteReportForm] en feuille modale. L'envoi la ferme côté ViewModel. */
@Composable
fun SiteReportSheet(
    hasOwner: Boolean,
    draft: SiteReportDraft,
    onDraft: (SiteReportDraft) -> Unit,
    onSubmit: () -> Unit,
    onDismiss: () -> Unit,
) {
    FruxModalBottomSheet(onDismiss = onDismiss, skipPartiallyExpanded = true) {
        SiteReportForm(
            hasOwner = hasOwner,
            draft = draft,
            onDraft = onDraft,
            onSubmit = onSubmit,
            modifier = Modifier.fillMaxWidth().padding(horizontal = FruxSpacing.md).padding(bottom = FruxSpacing.lg),
        )
    }
}
